import React, { useCallback, useState, useEffect, useRef } from "react"
import { useHistory } from "react-router-dom"
import Form from "@bappweb/form"
import Panel from "@bappweb/panel"
import TextField from "@bappweb/textfield"
import Button from "@bappweb/button"
import Image from "@bappweb/image"
import Captcha from "@smpu-web/nested/form/captcha"
import FormMessage from "@smpu-web/globalmodules/form-message/index"
import Page from "@smpu-web/globalmodules/page"
import { RegexpTypes, minLength, required } from "@bappweb/utils"
import {
  useGetTokenMutation,
  useGetCaptchaMutation,
  useRefreshCaptchaMutation,
  useStep1Mutation
} from "../../../api/services"
import "../assets/style.scss"
import "../../../../../assets/styles/style.scss"

function MainForm() {
  const history = useHistory()
  const refForm = useRef(null)

  const [token, setToken] = useState<string>('')
  const [posCekNo, setPosCekNo] = useState<string>('')

  const [captchaValues, setCaptchaValues] = useState({
    captchaData: "",
    captcha: "",
    showCaptcha: false,
    captchaValidate: true
  })

  const [error, setError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)

  const [getCaptcha] = useGetCaptchaMutation();
  useEffect(() => {
    if (captchaValues.captchaData === "" && token !== "") {
      setIsFetching(true)
      getCaptcha({
        posCekNo: posCekNo,
        TokenSessionId: token
      })
        .unwrap()
        .then((payload) => {
          if (!payload.IsError) {
            setError('')
            setCaptchaValues({
              ...captchaValues,
              showCaptcha: true,
              captchaData: payload.captchaData,              
              captchaValidate: true
            })
          }
          else {
            setError(payload.ErrorMessage ? payload.ErrorMessage : "")
          }
        })
        .finally(() => { setIsFetching(false) })
    }
  }, [captchaValues, getCaptcha, token])

  const [getToken] = useGetTokenMutation()
  const handleChange = useCallback((e) => {
    const name = e.target.name
    const value = e.target.value

    switch (name) {
      case "posCekNo":
        setPosCekNo(value)
        setToken('')
        setCaptchaValues({
          ...captchaValues,
          captchaData: "",
          showCaptcha: false
        })

        if (value.length === 22) {
          setIsFetching(true)
          getToken(value)
            .unwrap()
            .then((payload) => {
              if (!payload.IsError) {
                setError('')
                setToken(payload.tokenSessionId)
              } else {
                setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              }
            })
            .finally(() => { setIsFetching(false) })
        }
        break;
      case "captcha":
        setCaptchaValues({
          ...captchaValues,
          captcha: value,
          captchaValidate: true
        })
        break;
      default:
        break;
    }
  }, [captchaValues])

  const [refreshCaptcha] = useRefreshCaptchaMutation();
  const handleCaptchaRefresh = useCallback(() => {
    setIsFetching(true)
    refreshCaptcha({
      posCekNo: posCekNo,
      TokenSessionId: token,
    })
      .unwrap()
      .then((payload) => {
        if (!payload.IsError) {
          setError('')
          setCaptchaValues({
            ...captchaValues,
            captchaData: payload.captchaData,
            captchaValidate: true
          })
        } else {
          setError(payload.ErrorMessage ? payload.ErrorMessage : "")
        }
      })
      .finally(() => { setIsFetching(false) })
  }, [refreshCaptcha, posCekNo, captchaValues, token])


  const [step1] = useStep1Mutation()

  const handleSubmit = useCallback(
    (e?: React.BaseSyntheticEvent, values?: Record<string, any>) => {
      if (posCekNo && captchaValues.captcha !== "") {
        setIsFetching(true)
        step1({
          posCekNo: posCekNo,
          phoneLast4Number: values?.phoneLast4Number,
          captcha: captchaValues.captcha.toUpperCase(),
          TokenSessionId: token
        })
          .unwrap()
          .then((payload) => {
            if (!payload.IsError) {
              setError(null)
              history.push("/details/smsotp")
            } else {
              setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              window.scrollTo({
                top: 0
              })
              refreshCaptcha({
                posCekNo: posCekNo,
                TokenSessionId: token
              })
                .unwrap()
                .then((payload) => {
                  if (!payload.IsError) {
                    setCaptchaValues({
                      ...captchaValues,
                      captcha: "",
                      showCaptcha: true,
                      captchaData: payload.captchaData,
                      captchaValidate: false
                    })
                  } else {
                    setError(payload.ErrorMessage ? payload.ErrorMessage : "")
                  }
                })
                .finally(() => {
                  setIsFetching(false)
                })
            }
          })
          .finally(() => {
            setIsFetching(false)
          })
      }
    },
    [captchaValues, step1, refreshCaptcha, history]
  )

  return (
    <Page>
      <Form onSubmit={handleSubmit} onChange={handleChange} grid={false} ref={refForm}>
        <Panel hasFooter connected={false} loading={isFetching}>
          <FormMessage className={"form-msg-error"} error={error}></FormMessage>
          <Panel.Body>
            <Image path={"./img/common/akbank_logo.png"} className="landing-header-logo" />
            <TextField
              type="text"
              name="posCekNo"
              label="POS Bloke Transfer No"
              maxLength={22}
              validator={RegexpTypes.Numeric}
              validations={[required("Lütfen POS Bloke Transfer numarasını giriniz."), minLength(22,"Lütfen 22 haneli POS Bloke Transfer No'nuzu giriniz.")]}
            />
            <TextField
              label='Cep Telefon Son 4 Hane'
              type="text"
              mask="9999"
              name="phoneLast4Number"
              maxLength={4}
              validations={[required("Lütfen cep telefonun son 4 hanesini giriniz."), minLength(4,"Lütfen cep telefonun son 4 hanesini giriniz.")]}
            />
            {captchaValues.showCaptcha && (
              <Captcha
                label="Güvenlik Kodu"
                id="captcha"
                type="text"
                name="captcha"
                placeholder="Güvenlik Kodu"
                captcha={captchaValues.captchaData}
                value={captchaValues.captcha}
                captchaValidate = {captchaValues.captchaValidate}
                maxLength={4}
                maxRefreshCount={3}
                refreshClick={handleCaptchaRefresh}
                validations={[required("Lütfen güvenlik kodunu giriniz.")]}
              />
            )}
          </Panel.Body>
          <Panel.Footer>
            <Panel.RightFooter>
              <Button text="DEVAM" type="submit" fetching = {isFetching} disabled={isFetching} 
               onClick={() => 
                setCaptchaValues({
                    ...captchaValues,
                    captchaValidate: true
                  })
                }  />
            </Panel.RightFooter>
          </Panel.Footer>
        </Panel>
      </Form>
    </Page>
  )
}

export default MainForm
